import React, { useEffect, useCallback, useState } from 'react';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Input, Checkbox, Form, message } from 'antd';
import { useApolloClient, gql } from '@apollo/client';
import { email as emailRegex } from '@xbcb/regex';
import { logout, Cognito } from '@xbcb/aws-utils';
import styled from 'styled-components';
import { FormItem } from '@xbcb/form-item-components';
import { useLocation, useHistory } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import { LOGIN_CHANGE } from 'actions';
import inltLogin from 'libs/inltLogin';
import { reportError } from '@xbcb/ui-utils';
import { DataCyPrefix } from '@xbcb/ui-types';

const DECRYPT_LOGIN_LINK = gql`
  query DecryptLoginLink($linkHash: String!) {
    decryptLoginLink(linkHash: $linkHash) {
      email
      password
    }
  }
`;

const StyledForm = styled(Form)`
  max-width: 324px;
  width: 100%;
  margin: 25px;

  span,
  a,
  input {
    font-size: 14px;
  }

  input {
    /* height: 40px; */
  }

  button {
    height: 40px;

    span {
      font-size: 16px;
    }
  }

  &&& .login-form-forgot {
    float: right;
    border: none;
    height: 32px;
    padding: 0;
    margin: 0 var(--space-3);
    box-shadow: none;
  }

  .login-form-button {
    margin-top: var(--space-4);
    width: 100%;
  }

  .login-form-remember {
    float: left;
    span {
      font-size: 16px !important;
      font-weight: var(--font-weight-2) !important;
    }
  }
`;

const LoginForm = () => {
  const location = useLocation();
  const history = useHistory();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  // This will allow user to attempt login once
  const [passwordAttemptsExceeded, setPasswordAttemptsExceeded] =
    useState(false);
  const change = useCallback(
    (params: any) => dispatch(LOGIN_CHANGE(params)),
    [dispatch],
  );
  const submit = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    let { email, password, remember } = (await form.validateFields()) as {
      email: string;
      password: string;
      remember?: boolean;
    };
    let cognitoAttributes;
    try {
      await logout();
      email = email.toLowerCase();
      const {
        jwtToken,
        verification,
        delivery,
        user,
        newPassword,
        userAttributes,
      } = await Cognito.signInUser(
        {
          password,
          email,
        },
        {
          userPasswordAuthenticationFlowType: true,
        }, // while we are allowing legacy users to migrate to CBMS, we must use USER_PASSWORD_AUTH authentication flow type.
        // See: https://docs.aws.amazon.com/cognito/latest/developerguide/cognito-user-pools-import-using-lambda.html
      );
      if (jwtToken) {
        await inltLogin({
          dispatch,
          location,
          history,
          remember,
          userToken: jwtToken,
        });
      } else if (verification) {
        change({
          form: 'verification',
          delivery,
          user,
          remember,
        });
        setLoading(false);
      } else if (newPassword) {
        change({
          form: 'newPassword',
          user,
          userAttributes,
        });
        setLoading(false);
      }
    } catch (e: any) {
      let msg = e?.message;

      if (msg === 'Password attempts exceeded') {
        setPasswordAttemptsExceeded(true);
      }
      // dont let them enumerate users
      if (e?.code === 'UserNotFoundException' && !passwordAttemptsExceeded) {
        msg = 'Incorrect username or password.';
      }
      if (
        e.code === 'NotAuthorizedException' &&
        location.search.startsWith('?link=')
      ) {
        msg = 'Incorrect password, please use Forget Password';
        history.push('/login');
        setLoading(true);
      }
      if (
        !['UserNotFoundException', 'NotAuthorizedException'].includes(e?.code)
      ) {
        reportError(e);
      }
      if (msg === 'Unknown error, the response body from fetch is: undefined') {
        message.error(
          'Our login provider is experiencing issues and has been notified. We apologize for the inconvenience.',
          5.0,
        );
      } else {
        message.error(msg, 3.5);
      }
      setLoading(false);
    }
  }, [change, dispatch, form, history, loading, location]);

  const handleForgot = async () => {
    setLoading(true);
    try {
      await form.validateFields(['email']);
      const email = form.getFieldValue('email');
      try {
        const { delivery, user } = await Cognito.forgotPassword(email);
        change({
          form: 'verification',
          flow: 'forgot',
          delivery,
          user,
        });
        setLoading(false);
      } catch (e) {
        let msg = (e as any).message;
        if (msg.indexOf('not found') !== -1) {
          msg = 'User not not exist.';
        }
        if ((e as any).code === 'UserNotFoundException') {
          reportError(e);
        }
        message.error(msg, 3.5);
        setLoading(false);
      }
    } catch (errorInfo) {
      setLoading(false);
      message.error('Please enter your email to recover your password');
    }
  };

  const apolloClient = useApolloClient();

  useEffect(() => {
    if (
      !loading &&
      !passwordAttemptsExceeded &&
      location.search.startsWith('?link=')
    ) {
      const decryptLinkHash = async () => {
        const linkHash = location.search.split('=')[1];
        const { data, errors } = await apolloClient.query({
          query: DECRYPT_LOGIN_LINK,
          variables: { linkHash },
        });
        if (data?.decryptLoginLink && !errors) {
          const { email, password } = data.decryptLoginLink;
          form.setFieldsValue({ email, password });
          logout();
          submit();
        }
      };
      decryptLinkHash();
    }
  }, [apolloClient, form, loading, location.search, submit]);

  return (
    <StyledForm form={form} onFinish={submit}>
      <Form.Item>
        <h2>Log in to continue</h2>
      </Form.Item>
      <FormItem
        name="email"
        rules={[{ required: true, message: ' ', pattern: emailRegex }]}
        data-cy={DataCyPrefix.EMAIL}
      >
        <Input
          disabled={loading}
          prefix={<UserOutlined style={{ fontSize: 13 }} />}
          placeholder="Email Address"
          spellCheck={false}
        />
      </FormItem>
      <FormItem
        name="password"
        rules={[{ required: true, message: ' ' }]}
        data-cy={DataCyPrefix.PASSWORD}
      >
        <Input
          disabled={loading}
          prefix={<LockOutlined style={{ fontSize: 13 }} />}
          type="password"
          placeholder="Password"
          spellCheck={false}
        />
      </FormItem>
      <FormItem
        $inline
        name="remember"
        valuePropName="checked"
        $removeSpaceBottom
      >
        <Checkbox disabled={loading} className="login-form-remember">
          Remember me
        </Checkbox>
      </FormItem>
      <Button
        className="login-form-forgot"
        disabled={loading}
        onClick={handleForgot}
      >
        Forgot password
      </Button>
      <Button
        data-cy={DataCyPrefix.LOGIN_BUTTON}
        type="primary"
        htmlType="submit"
        className="login-form-button"
        loading={loading}
        disabled={passwordAttemptsExceeded}
      >
        {passwordAttemptsExceeded ? (
          <b>Maximum Password Attempts Exceeded</b>
        ) : (
          'Log In'
        )}
      </Button>
    </StyledForm>
  );
};

export default LoginForm;
